import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import FeatureInfoButton from "../components/FeatureInfoButton";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import PropertyCard from "../components/properties/PropertyCard";
import PropertyFormDrawer from "../components/properties/PropertyFormDrawer";
import ListingModal from "../components/properties/ListingModal";
import { BLANK_PROPERTY } from "../components/properties/shared";

/**
 * Properties page — the user's ledger. This component owns:
 *   - the list of properties + status filter
 *   - opening/closing the add/edit drawer
 *   - opening/closing the "list for rent" drawer
 *
 * UI pieces (PropertyCard, PropertyFormDrawer, ListingModal) live in
 * /components/properties so this file stays focused on data flow.
 */
export default function Properties() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);     // editing form payload (null = closed)
  const [isEdit, setIsEdit] = useState(false);
  const [cities, setCities] = useState([]);
  const [filter, setFilter] = useState("all"); // all | evaluating | owned | sold
  const [listingFor, setListingFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/properties");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/presets/cities").then(({ data }) => setCities(data.cities || [])).catch(() => {});
  }, [load]);

  const openNew = () => { setForm({ ...BLANK_PROPERTY }); setIsEdit(false); };
  const openEdit = (p) => { setForm({ ...p }); setIsEdit(true); };
  const close = () => { setForm(null); setIsEdit(false); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = sanitizePayload(form);
      if (isEdit) {
        await api.put(`/properties/${form.id}`, payload);
        toast.success("Property updated");
      } else {
        await api.post("/properties", payload);
        toast.success("Property saved");
      }
      close();
      load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const filtered = items.filter((p) => {
    const status = p.status || "evaluating";
    return filter === "all" ? true : status === filter;
  });
  const counts = {
    all: items.length,
    evaluating: items.filter((p) => (p.status || "evaluating") === "evaluating").length,
    owned: items.filter((p) => p.status === "owned").length,
    sold: items.filter((p) => p.status === "sold").length,
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-10" data-testid="properties-page">
      <Header onAdd={openNew} />

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState onAdd={openNew} />
      ) : (
        <>
          <FilterTabs filter={filter} setFilter={setFilter} counts={counts} />
          {filtered.length === 0 ? (
            <div className="card-flat p-12 text-center text-muted-foreground" data-testid="filter-empty">
              No {filter} properties yet.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <PropertyCard
                  key={p.id}
                  p={p}
                  onEdit={openEdit}
                  onDelete={remove}
                  onListForRent={setListingFor}
                />
              ))}
            </div>
          )}
        </>
      )}

      {form && (
        <PropertyFormDrawer
          form={form}
          setForm={setForm}
          isEdit={isEdit}
          cities={cities}
          onClose={close}
          onSubmit={save}
        />
      )}

      {listingFor && <ListingModal property={listingFor} onClose={() => setListingFor(null)} />}
    </div>
  );
}

/** Coerce form strings → numbers/null exactly the way the previous handler did. */
function sanitizePayload(form) {
  const payload = { ...form };
  delete payload.id;
  delete payload.user_id;
  delete payload.created_at;
  [
    "price", "area_sqft", "down_payment", "loan_rate", "loan_tenure_years",
    "maintenance_monthly", "property_tax_yearly", "expected_appreciation",
    "rental_yield", "score_location", "score_amenities", "score_safety",
    "score_commute", "score_resale", "monthly_rent_income",
  ].forEach((k) => { payload[k] = Number(payload[k] || 0); });
  ["purchase_price", "current_value", "current_loan_balance", "sold_price"].forEach((k) => {
    payload[k] = payload[k] === "" || payload[k] === null || payload[k] === undefined ? null : Number(payload[k]);
  });
  if (!payload.purchase_date) payload.purchase_date = null;
  if (!payload.sold_date) payload.sold_date = null;
  payload.rented = !!payload.rented;
  return payload;
}

function Header({ onAdd }) {
  return (
    <div className="flex items-end justify-between mb-8">
      <div>
        <div className="eyebrow mb-2">Your candidates</div>
        <h1 className="font-serif text-3xl sm:text-5xl inline-flex items-center gap-2">Property ledger. <FeatureInfoButton slug="properties" /></h1>
        <p className="text-muted-foreground mt-3 max-w-xl">
          Every flat, villa or plot on your shortlist — with the numbers and your gut-feel
          scores sitting side by side.
        </p>
      </div>
      <button
        onClick={onAdd}
        className="btn-primary px-5 py-3 text-sm inline-flex items-center gap-2"
        data-testid="add-property-button"
      >
        <Plus size={16} weight="bold" /> Add property
      </button>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="card-flat p-12 text-center">
      <div className="font-serif text-3xl mb-3">Nothing added yet.</div>
      <p className="text-muted-foreground mb-6">Add your first candidate to unlock scoring and comparisons.</p>
      <button onClick={onAdd} className="btn-primary px-5 py-3 text-sm inline-flex items-center gap-2">
        <Plus size={16} weight="bold" /> Add property
      </button>
    </div>
  );
}

function FilterTabs({ filter, setFilter, counts }) {
  return (
    <div className="border-b hairline mb-6 flex gap-1">
      {[
        ["all", "All"],
        ["evaluating", "Evaluating"],
        ["owned", "Owned"],
        ["sold", "Sold"],
      ].map(([id, label]) => (
        <button
          key={id}
          onClick={() => setFilter(id)}
          data-testid={`filter-${id}`}
          className={`px-4 py-2.5 text-sm -mb-px border-b-2 transition ${
            filter === id
              ? "border-[hsl(var(--primary))] text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
        >
          {label} <span className="text-muted-foreground ml-1">({counts[id]})</span>
        </button>
      ))}
    </div>
  );
}
